import { useState, useMemo, MouseEventHandler } from 'react';
import { localizeDateTime } from 'utils/l10n';
import { Link } from 'ui/links';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { useGraphql } from 'utils/hooks/useGraphql';
import { fetchPredictiveGoalUsers } from 'api/queries/predictiveAudiences';
import { TABLE_SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { Banner } from 'ui/banner';
import { BannerVariants } from 'ui/banner/types';
import { Badge } from 'ui/badge';
import { LastModifiedCell } from 'ui/indexPages/tables/cells/LastModifiedCell';
import { AlignTypes } from 'ui/tableHeader';
import type { ColumnData } from 'ui/tableHeader/constants';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer';
import { createPageActions } from '../createPageActions';
import {
  PredictiveAudienceGoal,
  PredictiveGoalsTableProps,
  User,
  PredictiveGoalsTableColValues,
  UserAvatarCell,
} from '../../types';
import { ensureString, parseLastRefreshForDisplay, removeTimeZoneFromDateStamp } from '../../utils';
import {
  createGoalUrlByErrorStatus,
  shouldDisableDeleteByStatus,
  shouldDisableEditByStatus,
  useGoalManagePermissionVerifier,
} from '../../goalUtils';
import { NeuralNetworkSvg } from '../../assets/NeuralNetworkSvg';
import { ModelDetailColumnHeader } from '../ModelDetailColumnHeader';
import {
  BorderRadiusSkeleton,
  PredictiveGoalCaption,
  PredictiveGoalsLastModifiedTableCell,
  PredictiveGoalSpacedShadowContainer,
  PredictiveGoalsTableCell,
  PredictiveGoalTableHeaderContainer,
  PredictiveGoalTableRowContainer,
  PredictiveStrengthTableCell,
  StyledDisabledText,
  StyledMonospaceForCell,
} from '../Styled';
import { customPGSortByObjValue } from '../../tableUtils';
import { NoGoalsEmptyState } from '../NoGoalsEmptyState';
import {
  GOAL_DELETE_ERROR,
  GOAL_STATUS_BADGE_CONFIG,
  LAST_REFRESH_TOOLTIP,
  PREDICTIVE_STRENGTH_TOOLTIP,
  PREDICTIVE_STRENGTH_CELL_VISIBLE_STATUSES,
  TEST_IDS,
  USER_PROFILE_FIELD,
  DELETE_PREDICTIVE_GOALS_TOAST_MESSAGE,
} from '../../constants';
import { DeleteModal } from '../modals/DeleteModal';
import { useGoalDeleteMutation } from '../hooks/useGoalDeleteMutation';
import { PredictiveStrengthMeterIcon } from '../PredictiveStrengthMeterIcon';

const displayText = {
  tableHeaders: {
    predictiveGoal: 'Predictive goal',
    propertyName: USER_PROFILE_FIELD,
    status: 'Status',
    lastModified: 'Last modified',
    lastRefresh: 'Last refresh',
    predictiveStrength: 'Predictive strength',
  },
  toggleTips: {
    lastRefresh: <>{LAST_REFRESH_TOOLTIP}</>,
    predictiveStrength: <>{PREDICTIVE_STRENGTH_TOOLTIP}</>,
  },
};

export type RowProps = {
  row: PredictiveAudienceGoal;
};

export const createCellActions = (
  handleOnDelete: (goal: PredictiveAudienceGoal) => MouseEventHandler,
  handleOnEdit: (goal: PredictiveAudienceGoal) => MouseEventHandler,
  shouldDisableDeleteForGoal: (goal: PredictiveAudienceGoal) => boolean,
  shouldDisableEditForGoal: (goal: PredictiveAudienceGoal) => boolean,
  canManageGoals: boolean,
) => {
  return ({ row }: RowProps) => {
    const MenuActions = createPageActions({
      isDeleteDisabled: shouldDisableDeleteForGoal(row),
      isEditDisabled: shouldDisableEditForGoal(row),
      onDelete: handleOnDelete(row),
      onEdit: handleOnEdit(row),
      canManageGoals,
    });
    return (
      <PredictiveGoalsTableCell>
        <MenuActions />
      </PredictiveGoalsTableCell>
    );
  };
};

export type GoalRowProps = {
  row: PredictiveAudienceGoal;
};

export const NameRow = ({ row }: GoalRowProps) => (
  <PredictiveGoalsTableCell>
    <Link inTable useReactRouter href={createGoalUrlByErrorStatus(row)}>
      {row.name}
    </Link>
    <PredictiveGoalCaption>{`ID ${row.id}`}</PredictiveGoalCaption>
  </PredictiveGoalsTableCell>
);

export const ContactRow = ({ row }: GoalRowProps) => (
  <PredictiveGoalsTableCell>
    <StyledMonospaceForCell>{row.userProfileField}</StyledMonospaceForCell>
  </PredictiveGoalsTableCell>
);

export const StatusRow = ({ row }: GoalRowProps) => (
  <PredictiveGoalsTableCell>
    {row.status && (
      <Badge variant={GOAL_STATUS_BADGE_CONFIG[row.status].variant}>
        {GOAL_STATUS_BADGE_CONFIG[row.status].text}
      </Badge>
    )}
  </PredictiveGoalsTableCell>
);

export const PredictiveStrengthCell = ({ row }: GoalRowProps) => {
  const modelQuality = row?.modelDetails?.model_quality;
  const isStrengthCellToBeVisibile = PREDICTIVE_STRENGTH_CELL_VISIBLE_STATUSES.includes(
    row?.status,
  );

  return (
    <PredictiveStrengthTableCell>
      {modelQuality && isStrengthCellToBeVisibile ? (
        <div data-test={TEST_IDS.PredictiveGoalsStrengthCell}>
          {modelQuality}
          <PredictiveStrengthMeterIcon strength={modelQuality} />
        </div>
      ) : (
        <StyledDisabledText>N/A</StyledDisabledText>
      )}
    </PredictiveStrengthTableCell>
  );
};

export const setUserInfoForModifiedRow =
  (userInfoLookup: any) =>
  ({ row }: GoalRowProps) => (
    <PredictiveGoalsLastModifiedTableCell>
      <LastModifiedCell
        createdBy={
          userInfoLookup[row.createdBy] || {
            fullName: row.createdBy,
            emailAddress: row.createdBy,
          }
        }
        modifiedBy={
          userInfoLookup[row.updatedBy] || {
            fullName: row.updatedBy,
            emailAddress: row.updatedBy,
          }
        }
        createdAt={localizeDateTime(new Date(removeTimeZoneFromDateStamp(row.createdAt.timestamp)))}
        modifiedAt={localizeDateTime(
          new Date(removeTimeZoneFromDateStamp(row.updatedAt.timestamp)),
        )}
      />
    </PredictiveGoalsLastModifiedTableCell>
  );

export const LastRefreshCell = ({ row }: GoalRowProps) => {
  return (
    <PredictiveGoalsTableCell data-test={TEST_IDS.LastRefreshCell}>
      {row && row.lastScoredAt ? (
        parseLastRefreshForDisplay(ensureString(row.lastScoredAt.timestamp))
      ) : (
        <StyledDisabledText>N/A</StyledDisabledText>
      )}
    </PredictiveGoalsTableCell>
  );
};

export const getFormattedAvatarUserLookup = (userData: any): { [key: string]: UserAvatarCell } => {
  return userData?.users.reduce((userMapping: { [key: string]: UserAvatarCell }, user: User) => {
    const updatedUser = { ...user, emailAddress: user.id };
    return { ...userMapping, [user.id]: updatedUser };
  }, {});
};

export const PredictiveGoalsTable = ({
  loading,
  error,
  rows,
  onMutation,
  showPlaceholderContent,
}: PredictiveGoalsTableProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState({} as PredictiveAudienceGoal);
  const [sortedBy, setSortedBy] = useState('name');
  const [sortDirection, setSortDirection] = useState<string>(TABLE_SORT_DIRECTIONS.DESCENDING);
  const { data: userData } = useGraphql(fetchPredictiveGoalUsers);
  const [deleteGoalQuery] = useGoalDeleteMutation(
    () => {
      dispatch(addPositiveToast(DELETE_PREDICTIVE_GOALS_TOAST_MESSAGE));
      onMutation();
    },
    () => {
      dispatch(addNegativeToast(GOAL_DELETE_ERROR));
    },
  );
  const canManageGoals = useGoalManagePermissionVerifier();

  const BASE_COLS = useMemo(() => {
    const userInfoLookup = getFormattedAvatarUserLookup(userData) || {};
    return [
      {
        name: displayText.tableHeaders.predictiveGoal,
        value: PredictiveGoalsTableColValues.name,
        align: 'flex-start',
        RowCell: NameRow,
      } as ColumnData,
      {
        name: displayText.tableHeaders.propertyName,
        value: PredictiveGoalsTableColValues.userProfileField,
        align: 'flex-start',
        RowCell: ContactRow,
      } as ColumnData,
      {
        name: displayText.tableHeaders.status,
        value: PredictiveGoalsTableColValues.status,
        align: 'flex-start',
        RowCell: StatusRow,
      } as ColumnData,
      {
        name: displayText.tableHeaders.lastModified,
        value: PredictiveGoalsTableColValues.updatedAt,
        align: 'flex-start',
        RowCell: setUserInfoForModifiedRow(userInfoLookup),
      } as ColumnData,
      {
        name: displayText.tableHeaders.lastRefresh,
        value: PredictiveGoalsTableColValues.lastTrainedAt,
        align: 'flex-start',
        RowCell: LastRefreshCell,
        HeaderCell: () => (
          <ModelDetailColumnHeader
            name={displayText.tableHeaders.lastRefresh}
            value={PredictiveGoalsTableColValues.lastTrainedAt}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            sortedBy={sortedBy}
            setSortedBy={setSortedBy}
            toggleTipContent={displayText.toggleTips.lastRefresh}
          />
        ),
      } as ColumnData,
      {
        name: displayText.tableHeaders.predictiveStrength,
        value: PredictiveGoalsTableColValues.modelDetails,
        align: 'flex-start',
        sortDirection: { sortDirection },
        setSortDirection: { setSortDirection },
        sortedBy: { sortedBy },
        setSortedBy: { setSortedBy },
        RowCell: PredictiveStrengthCell,
        HeaderCell: () => (
          <ModelDetailColumnHeader
            name={displayText.tableHeaders.predictiveStrength}
            value={PredictiveGoalsTableColValues.modelDetails}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
            sortedBy={sortedBy}
            setSortedBy={setSortedBy}
            toggleTipContent={displayText.toggleTips.predictiveStrength}
          />
        ),
      } as ColumnData,
    ];
  }, [userData, sortedBy, sortDirection]);

  const handleOnDelete = (row: PredictiveAudienceGoal) => {
    return () => {
      setSelectedGoal(row);
      setIsDeleteModalOpen(true);
    };
  };

  const handleOnEdit = (row: PredictiveAudienceGoal) => {
    return () => {
      setSelectedGoal(row);
      history.push(`/predictive-audiences/goals/${row.id}/edit`);
    };
  };

  const actionsColumn = {
    name: '',
    value: 'id',
    align: 'flex-start' as AlignTypes,
    sortable: false,
    RowCell: createCellActions(
      handleOnDelete,
      handleOnEdit,
      shouldDisableDeleteByStatus,
      shouldDisableEditByStatus,
      canManageGoals,
    ),
  };

  const deleteGoal = () => {
    deleteGoalQuery(selectedGoal.id);
    setIsDeleteModalOpen(false);
  };

  const columns = [...BASE_COLS, actionsColumn];
  const sortedRows = customPGSortByObjValue(
    Object.values(rows),
    sortedBy as keyof typeof PredictiveGoalsTableColValues,
    sortDirection,
  );

  if (error) {
    return <Banner variant={BannerVariants.ERROR}>Unable to load existing goals!</Banner>;
  }

  if (loading) {
    return <BorderRadiusSkeleton height={300} />;
  }

  if (showPlaceholderContent || rows.length === 0) {
    return (
      <>
        <PredictiveGoalSpacedShadowContainer>
          <PredictiveGoalTableHeaderContainer
            columns={columns}
            sortedBy={sortedBy}
            setSortedBy={setSortedBy}
            sortDirection={sortDirection}
            setSortDirection={setSortDirection}
          />
        </PredictiveGoalSpacedShadowContainer>
        <NoGoalsEmptyState Icon={NeuralNetworkSvg} title="No predictive goals yet" />
      </>
    );
  }

  const deleteGoalModal = isDeleteModalOpen && (
    <DeleteModal
      isModalOpen={isDeleteModalOpen}
      onClose={() => setIsDeleteModalOpen(false)}
      deleteGoal={deleteGoal}
      goal={selectedGoal}
    />
  );

  return (
    <>
      <PredictiveGoalSpacedShadowContainer>
        <PredictiveGoalTableHeaderContainer
          columns={columns}
          sortedBy={sortedBy}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
        />
        <PredictiveGoalTableRowContainer rows={sortedRows} columns={columns} />
      </PredictiveGoalSpacedShadowContainer>
      {deleteGoalModal}
    </>
  );
};
